import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CheckCircle2, Circle, LogIn, LogOut, MessageSquare, ShieldCheck } from 'lucide-react';
import { t } from '../../utils/i18n';
import { displayUserEmail } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';
import { useDmPersona } from '../../context/DmPersonaContext';
import { getUsers, getUsersSummary } from '../../api/authAdmin';
import UserAvatar from '../../components/UserAvatar';
import DmUserLoginModal from '../../components/DmUserLoginModal/DmUserLoginModal';

const PAGE_SIZE = 10;

function formatDateTime(value) {
  if (value == null || value === '') return '-';
  if (typeof value !== 'string') return String(value);
  return value.replace('T', ' ').slice(0, 19);
}

function getAccountStatusLabel(status) {
  return status === 1 ? t('user.accountStatus.active') : t('user.accountStatus.inactive');
}

function getLoginStatusLabel(status) {
  return status === 1 ? t('user.loginStatus.loggedIn') : t('user.loginStatus.loggedOut');
}

function getAccountTypeLabel(type) {
  switch (type) {
    case 'OFFICIAL': return t('user.accountType.official');
    case 'ADMIN': return t('user.accountType.admin');
    case 'BOT': return t('user.accountType.bot');
    case 'REVIEWER': return t('user.accountType.reviewer');
    case 'TESTER': return t('user.accountType.tester');
    case 'USER': return t('user.accountType.user');
    default: return t('user.accountType.user');
  }
}

export default function DmUsers() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token: adminToken, logout: adminLogout } = useAuth();
  const {
    userId: personaUserId,
    username: personaUsername,
    isAuthenticated: hasPersona,
    logout: personaLogout,
  } = useDmPersona();

  const [page, setPage] = useState(1);
  const [excludeResigned, setExcludeResigned] = useState(true);
  const [modalTarget, setModalTarget] = useState(null);

  const usersQuery = useQuery({
    queryKey: ['dm-users', page, PAGE_SIZE, excludeResigned],
    queryFn: () => getUsers(page, PAGE_SIZE, adminToken, '', { excludeResigned }),
    enabled: !!adminToken,
    placeholderData: keepPreviousData,
  });

  const summaryQuery = useQuery({
    queryKey: ['dm-users-summary'],
    queryFn: () => getUsersSummary(adminToken),
    enabled: !!adminToken,
  });

  useEffect(() => {
    const err = usersQuery.error || summaryQuery.error;
    if (err?.status === 401) {
      adminLogout();
      navigate('/login', { replace: true });
    }
  }, [usersQuery.error, summaryQuery.error, adminLogout, navigate]);

  const {
    items: paginatedUsers = [],
    total_count: totalCount = 0,
    page: currentPage = 1,
    page_size: pageSize = PAGE_SIZE,
  } = usersQuery.data || {};
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const summary = useMemo(() => {
    const s = summaryQuery.data;
    if (!s) {
      return {
        totalUsers: 0,
        activeUsers: 0,
        inactiveUsers: 0,
        loggedInUsers: 0,
        loggedOutUsers: 0,
        accountTypeCounts: { USER: 0, OFFICIAL: 0, ADMIN: 0, BOT: 0, REVIEWER: 0, TESTER: 0 },
      };
    }
    return {
      totalUsers: s.total_users ?? 0,
      activeUsers: s.active_users ?? 0,
      inactiveUsers: s.inactive_users ?? 0,
      loggedInUsers: s.logged_in_users ?? 0,
      loggedOutUsers: s.logged_out_users ?? 0,
      accountTypeCounts: s.account_type_counts ?? { USER: 0, OFFICIAL: 0, ADMIN: 0, BOT: 0, REVIEWER: 0, TESTER: 0 },
    };
  }, [summaryQuery.data]);

  const handleExcludeResignedChange = (e) => {
    setExcludeResigned(e.target.checked);
    setPage(1);
  };

  const handlePrev = () => setPage((prev) => Math.max(1, prev - 1));
  const handleNext = () => setPage((prev) => Math.min(totalPages, prev + 1));

  const handleRowSelect = (user) => {
    const uid = user.user_id ?? user.UserID;
    const uname = user.username ?? user.Username ?? '';
    setModalTarget({ userId: uid, username: uname, ...user });
  };

  const handlePersonaLogout = () => {
    personaLogout();
  };

  const handleModalSuccess = () => {
    setModalTarget(null);
    const params = new URLSearchParams(location.search);
    const redirect = params.get('redirect');
    if (
      redirect &&
      (redirect.startsWith('/dm/') ||
        redirect.startsWith('/celeb-room/') ||
        redirect.startsWith('/game/'))
    ) {
      navigate(redirect);
    } else {
      navigate('/dm/conversations');
    }
  };

  const isLoading = usersQuery.isLoading || summaryQuery.isLoading;
  const isError = usersQuery.isError || summaryQuery.isError;
  const errorMessage = usersQuery.error?.message || summaryQuery.error?.message;

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-10 2xl:px-16 bg-dark-bg">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-dark-text flex items-center gap-2">
              {t('dm.users.title')}
              <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-primary/20 text-primary border border-primary/40 flex items-center gap-1">
                <ShieldCheck size={12} /> ADMIN
              </span>
            </h1>
            <p className="text-dark-text-secondary text-sm mt-1">{t('dm.users.description')}</p>
          </div>

          {hasPersona && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 text-xs text-dark-text-secondary px-3 py-1.5 rounded-full border border-primary/40 bg-dark-card">
                <MessageSquare size={14} className="text-primary" />
                <span>
                  {t('dm.users.activePersona', {
                    username: personaUsername || '—',
                    userId: personaUserId ?? '—',
                  })}
                </span>
              </div>
              <button
                type="button"
                onClick={handlePersonaLogout}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-red-500/40 text-red-300 text-xs hover:bg-red-500/10 transition-colors"
              >
                <LogOut size={14} />
                {t('dm.users.actions.signOut')}
              </button>
            </div>
          )}
        </div>

        <div
          className={`mb-6 p-4 rounded-xl border ${
            hasPersona ? 'border-primary/30 bg-primary/5' : 'border-primary/40 bg-dark-card'
          }`}
        >
          <ol className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm">
            <li className="flex items-center gap-2 text-primary">
              <CheckCircle2 size={18} />
              <span className="font-medium">{t('dm.users.steps.step1')}</span>
            </li>
            <span className="hidden sm:inline-block w-6 h-px bg-dark-border" />
            <li className="flex items-center gap-2">
              {hasPersona ? (
                <>
                  <CheckCircle2 size={18} className="text-primary" />
                  <span className="font-medium text-primary">
                    {t('dm.users.steps.step2DoneFull', {
                      username: personaUsername || '—',
                      userId: personaUserId ?? '—',
                    })}
                  </span>
                </>
              ) : (
                <>
                  <Circle size={18} className="text-primary animate-pulse" />
                  <span className="font-medium text-dark-text">{t('dm.users.steps.step2')}</span>
                  <span className="text-xs text-dark-text-secondary">
                    {t('dm.users.steps.step2Hint')}
                  </span>
                </>
              )}
            </li>
            <span className="hidden sm:inline-block w-6 h-px bg-dark-border" />
            <li className="flex items-center gap-2 text-dark-text-secondary">
              {hasPersona ? (
                <CheckCircle2 size={18} className="text-primary" />
              ) : (
                <Circle size={18} className="text-dark-text-muted" />
              )}
              <span className={hasPersona ? 'text-dark-text' : ''}>{t('dm.users.steps.step3')}</span>
            </li>
          </ol>
        </div>

        {isError && (
          <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {errorMessage}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-dark-card border border-dark-border rounded-lg p-4">
            <p className="text-xs text-dark-text-secondary mb-1">{t('user.summary.totalUsers')}</p>
            <p className="text-2xl font-semibold text-dark-text">
              {summaryQuery.isLoading ? '-' : summary.totalUsers}
            </p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-lg p-4">
            <p className="text-xs text-dark-text-secondary mb-1">{t('user.summary.accountStatus')}</p>
            <div className="flex items-baseline gap-4">
              <span className="text-sm text-primary">
                {t('user.summary.active')}: {summaryQuery.isLoading ? '-' : summary.activeUsers}
              </span>
              <span className="text-sm text-red-400">
                {t('user.summary.inactive')}: {summaryQuery.isLoading ? '-' : summary.inactiveUsers}
              </span>
            </div>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-lg p-4">
            <p className="text-xs text-dark-text-secondary mb-1">{t('user.summary.loginStatus')}</p>
            <div className="flex items-baseline gap-4">
              <span className="text-sm text-primary">
                {t('user.summary.loggedIn')}: {summaryQuery.isLoading ? '-' : summary.loggedInUsers}
              </span>
              <span className="text-sm text-dark-text-secondary">
                {t('user.summary.loggedOut')}: {summaryQuery.isLoading ? '-' : summary.loggedOutUsers}
              </span>
            </div>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-lg p-4">
            <p className="text-xs text-dark-text-secondary mb-1">{t('user.summary.accountType')}</p>
            <div className="flex flex-wrap gap-2 mt-1 text-xs text-dark-text-secondary">
              <span>{t('user.accountType.user')}: {summaryQuery.isLoading ? '-' : (summary.accountTypeCounts.USER ?? 0)}</span>
              <span>{t('user.accountType.official')}: {summaryQuery.isLoading ? '-' : (summary.accountTypeCounts.OFFICIAL ?? 0)}</span>
              <span>{t('user.accountType.admin')}: {summaryQuery.isLoading ? '-' : (summary.accountTypeCounts.ADMIN ?? 0)}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={excludeResigned}
              onChange={handleExcludeResignedChange}
              className="w-4 h-4 rounded border-dark-border bg-dark-surface accent-primary cursor-pointer"
            />
            <span className="text-sm text-dark-text-secondary hover:text-dark-text transition-colors">
              {t('dm.users.filter.excludeResigned')}
            </span>
          </label>
        </div>

        <div className="bg-dark-card border border-dark-border rounded-lg overflow-hidden">
          {isLoading ? (
            <p className="p-8 text-center text-dark-text-secondary">{t('user.table.loading')}</p>
          ) : paginatedUsers.length === 0 ? (
            <p className="p-8 text-center text-dark-text-secondary">{t('user.table.empty')}</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-dark-border">
                      <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm w-12">{t('user.table.avatar')}</th>
                      <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.userId')}</th>
                      <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.username')}</th>
                      <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.name')}</th>
                      <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.email')}</th>
                      <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.accountStatus')}</th>
                      <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.loginStatus')}</th>
                      <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.accountType')}</th>
                      <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.lastLogin')}</th>
                      <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.createdAt')}</th>
                      <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map((user) => {
                      const uid = user.user_id ?? user.UserID;
                      const uname = user.username ?? user.Username ?? '-';
                      const isCurrentPersona = hasPersona && Number(uid) === Number(personaUserId);
                      return (
                        <tr
                          key={uid}
                          className="border-b border-dark-border last:border-0 hover:bg-dark-surface/50"
                        >
                          <td className="py-3 px-4 align-middle"><UserAvatar user={user} size={32} /></td>
                          <td className="py-3 px-4 text-primary">{uid ?? '-'}</td>
                          <td className="py-3 px-4 text-dark-text">{uname}</td>
                          <td className="py-3 px-4 text-dark-text">{user.name ?? user.Name ?? '-'}</td>
                          <td className="py-3 px-4 text-dark-text">{displayUserEmail(user)}</td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                (user.account_status ?? user.AccountStatus) === 1
                                  ? 'bg-green-500/10 text-green-400'
                                  : 'bg-red-500/10 text-red-400'
                              }`}
                            >
                              {getAccountStatusLabel(user.account_status ?? user.AccountStatus ?? 0)}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                (user.login_status ?? user.LoginStatus) === 1
                                  ? 'bg-blue-500/10 text-blue-400'
                                  : 'bg-dark-surface text-dark-text-secondary'
                              }`}
                            >
                              {getLoginStatusLabel(user.login_status ?? user.LoginStatus ?? 0)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-dark-text-secondary text-sm">
                            {getAccountTypeLabel(user.account_type ?? user.AccountType ?? 'USER')}
                          </td>
                          <td className="py-3 px-4 text-dark-text-secondary text-sm whitespace-nowrap">
                            {formatDateTime(user.login_at ?? user.LoginAt)}
                          </td>
                          <td className="py-3 px-4 text-dark-text-secondary text-sm whitespace-nowrap">
                            {formatDateTime(user.created_at ?? user.CreatedAt)}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            {isCurrentPersona ? (
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/15 text-primary text-[11px] font-semibold border border-primary/40">
                                  <CheckCircle2 size={12} />
                                  {t('dm.users.actions.signedIn')}
                                </span>
                                <button
                                  onClick={handlePersonaLogout}
                                  className="inline-flex items-center gap-1 px-3 py-1 rounded border border-red-500/40 text-red-300 text-xs hover:bg-red-500/10 transition-colors"
                                >
                                  <LogOut size={14} />
                                  {t('dm.users.actions.signOut')}
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleRowSelect(user)}
                                className="inline-flex items-center gap-1 px-3 py-1 rounded border border-primary/40 text-primary text-xs hover:bg-primary/10 transition-colors"
                              >
                                <LogIn size={14} />
                                {t('dm.users.actions.signIn')}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between px-4 py-3 border-t border-dark-border text-sm text-dark-text-secondary">
                <span>{t('user.pagination.page', { page: currentPage, totalPages })}</span>
                <div className="space-x-2">
                  <button
                    className="px-3 py-1 rounded border border-dark-border disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
                    onClick={handlePrev}
                    disabled={page === 1}
                  >
                    {t('user.pagination.prev')}
                  </button>
                  <button
                    className="px-3 py-1 rounded border border-dark-border disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
                    onClick={handleNext}
                    disabled={page >= totalPages}
                  >
                    {t('user.pagination.next')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <DmUserLoginModal
        open={!!modalTarget}
        targetUser={modalTarget}
        onClose={() => setModalTarget(null)}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
}
